import { IconSolidGithub } from '@gonfalon/observability-ui/components/icons/IconSolidGithub';
import type { ButtonProps } from '@launchpad-ui/components';
import { Button } from '@launchpad-ui/components';
import { Icon } from '@launchpad-ui/icons';
import clsx from 'clsx';

import type { GitHubIntegration } from '../../hooks/useGitHubIntegration';

import styles from './ConnectGitHubButton.module.css';

const DEFAULT_LABELS = {
  'not-authorized': 'Sign in with GitHub',
  'not-installed': 'Connect GitHub',
  error: 'Connect GitHub',
} as const;

export interface ConnectGitHubButtonProps {
  integration: GitHubIntegration;
  /** Overrides the label the integration state would pick. */
  label?: string;
  /** Where GitHub returns to. Defaults to the current URL. */
  returnUrl?: string;
  isDisabled?: boolean;
  size?: ButtonProps['size'];
  variant?: ButtonProps['variant'];
  /** Fill the container, pushing the label and the external-link icon apart. */
  fullWidth?: boolean;
  showExternalIcon?: boolean;
  className?: string;
  /** Surface name attached to the connect analytics event. */
  trackingComponent?: string;
}

/**
 * The "GitHub isn't connected yet" affordance. Renders nothing when the integration is ready or
 * still resolving, so callers can drop it in beside the repo selector without repeating the status
 * check. A caller that wants to hold the space while status resolves renders its own placeholder —
 * the two look different enough that sharing this component's styling hooks caused more trouble
 * than it saved.
 */
export function ConnectGitHubButton({
  integration,
  label,
  returnUrl,
  isDisabled,
  size = 'medium',
  variant = 'default',
  fullWidth,
  showExternalIcon = true,
  className,
  trackingComponent,
}: ConnectGitHubButtonProps) {
  const { state, connect } = integration;
  const classes = clsx(className, fullWidth && styles.fullWidth);

  // Nothing to prompt for yet: GitHub is connected, or status hasn't come back.
  if (state === 'ready' || state === 'loading') {
    return null;
  }

  return (
    <Button
      variant={variant}
      size={size}
      isDisabled={isDisabled}
      className={classes}
      data-test-id="connect-github-button"
      onPress={() => connect({ returnUrl, component: trackingComponent })}
    >
      <span className={styles.content}>
        <IconSolidGithub size={14} />
        {label ?? DEFAULT_LABELS[state]}
      </span>
      {showExternalIcon && <Icon name="link-external" size="small" />}
    </Button>
  );
}
